import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useWidget } from "../../../providers/widget"
import { useLocaleDetection } from "../../../hooks/locale"
import { useLocalizedTranslation } from "../../../common/i18n"
import { useExplorer } from "../providers"
import { ExploreProperty, fetchQuote, QuoteRateplan, useFetchProperties, withBrowsePriceField } from "../data"
import { ExploreWidgetConfig } from "../config"
import PropertyCard from "./PropertyCard"
import PropertySkeleton from "./PropertySkeleton"
import Recommendations from "./Recommendations"
import ZeroResultsRescue from "./ZeroResultsRescue"
import AdjustedDatesNotice from "./AdjustedDatesNotice"
import WidgetDataError from "../../../components/WidgetDataError"
import MarketingCard from "./MarketingCard"
import {
  activeMarketingCards,
  computeMarketingCardPositions,
  createMarketingCardTracker,
} from "../marketingCards"
import { useMarketingCardImpressions } from "../hooks/useMarketingCardImpressions"
import { boundsFiltering } from "./map/initialView"
import { useCompareResults } from "../compare/useCompareResults"

type QuoteCache = Record<string, {
  rateplan: QuoteRateplan
  nights: number
} | 'loading' | 'failed'>

export default function PropertyGrid() {
  const { options, feedId, widgetId } = useWidget<ExploreWidgetConfig>()
  const { state, mapActions } = useExplorer()
  // #1262: grid chrome strings — operator value → shipped locale table → en_US.
  const { detectedLocale } = useLocaleDetection(options.language.useAutoDetectedLocale, options.language.locale)
  const { _t } = useLocalizedTranslation('explorer', options.translations, detectedLocale, options.language)
  const platformUuid = options.filter?.platform
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const [quoteCache, setQuoteCache] = useState<QuoteCache>({})
  const quoteQueueRef = useRef<ExploreProperty[]>([])
  const processingRef = useRef(false)

  // Merge map bounds into visitor filters when search-on-move is active
  const boundsString = boundsFiltering(state) && state.mapBounds
    ? `${state.mapBounds.west},${state.mapBounds.south},${state.mapBounds.east},${state.mapBounds.north}`
    : undefined

  // Column counts switch with the map (#564, WP: with/without-map columns):
  // a 2-column grid beside the open map can become 3 columns once it's hidden.
  const mapOpen = options.showMap === true && state.mapVisible
  const grid = options.grid
  const gridVars = {
    '--hfse-grid-columns': mapOpen ? (grid.columnsWithMap ?? grid.columns) : grid.columns,
    '--hfse-grid-columns-780': mapOpen ? (grid.columnsWithMap780 ?? grid.columns780) : grid.columns780,
    '--hfse-grid-columns-960': mapOpen ? (grid.columnsWithMap960 ?? grid.columns960) : grid.columns960,
    '--hfse-grid-gap': `${grid.gap}px`,
  } as React.CSSProperties

  const filtersWithBounds = useMemo(() => {
    const filters = { ...state.visitorFilters }
    if (boundsString) {
      filters.map_bounds = boundsString
    }
    // Price filter/sort/bounds use the column matching the browse (no-dates) price mode (#441).
    return withBrowsePriceField(filters, options)
  }, [state.visitorFilters, boundsString, options])

  // When full price is on for a date search, use the separate (smaller) page size if set
  // (WP: per_page_with_full_price) — per-property quotes are heavier, so fewer per page.
  const isDateQuery = Boolean(state.visitorFilters.checkin && state.visitorFilters.checkout)
  const shouldFetchFullPrice = options.fullPrice && isDateQuery
  const pageLimit = shouldFetchFullPrice && (options.perPageWithFullPrice ?? 0) > 0
    ? options.perPageWithFullPrice
    : options.filter.limit

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useFetchProperties(feedId, filtersWithBounds, pageLimit, platformUuid)

  const properties = data?.pages.flatMap((page) => page.result) ?? []
  const showFavoriteButton = options.features?.favorites ?? false
  // Compare Homes (#1713): reports the rows this grid rendered, hands back per-card props
  // (undefined unless compare mode is on). No request: the rows are already loaded.
  const compareFor = useCompareResults(properties, quoteCache, !isLoading)

  // Promo cards (#1145): placement is a pure function of (enabled cards,
  // frequency, loaded listings), recomputed every render — so the interleave
  // is identical across re-renders and infinite-scroll page loads with no DOM
  // diffing (the WP engine's MutationObserver defends against a problem a
  // declarative grid does not have). Slider mode and the no-results state
  // never reach this grid, matching WP's mode guard.
  const mc = options.marketingCards
  const promoCards = useMemo(() => (mc.enabled ? activeMarketingCards(mc.cards) : []), [mc.enabled, mc.cards])
  const promoPlacements = useMemo(
    () => computeMarketingCardPositions(promoCards.length, mc.frequency, properties.length),
    [promoCards.length, mc.frequency, properties.length],
  )
  const promoByPos = useMemo(() => new Map(promoPlacements.map((p) => [p.pos, p])), [promoPlacements])
  // Observer key (see useMarketingCardImpressions): slot + card id + the id
  // of the listing the card follows. Rows are keyed by property id, so a
  // result-set change with the same count remounts the card nodes — the
  // followed-listing id is what makes the key (and the observer) follow them.
  const promoKey = promoPlacements
    .map((p) => `${p.pos}:${properties[p.pos - 1]?.id ?? ''}:${promoCards[p.cardIndex].id}`)
    .join(',')
  const gridRef = useRef<HTMLDivElement | null>(null)
  // One tracker per mount = one page view: the impression de-dupe lifetime.
  const promoTracker = useMemo(
    () => createMarketingCardTracker({ widgetType: 'explorer', widgetId, feedId }),
    [widgetId, feedId],
  )
  useMarketingCardImpressions(gridRef, promoKey, promoTracker)
  const onPromoCtaClick = useCallback(
    (cardId: string, ordinal: number, url: string, target: Element) => {
      promoTracker.ctaClick(cardId, ordinal, url, target, (window as any).dataLayer)
    },
    [promoTracker],
  )

  // Process full price queue sequentially
  const processQueue = useCallback(async () => {
    if (processingRef.current || quoteQueueRef.current.length === 0) return
    processingRef.current = true

    const property = quoteQueueRef.current.shift()!
    if (!property.slug) {
      processingRef.current = false
      processQueue()
      return
    }
    setQuoteCache(prev => ({ ...prev, [property.id]: 'loading' }))

    try {
      const result = await fetchQuote(
        feedId,
        {
          property: property.slug,
          checkin: state.visitorFilters.checkin!,
          checkout: state.visitorFilters.checkout!,
          guests: state.visitorFilters.guests ?? state.visitorFilters.adults ?? 1,
        },
              )

      if (result.success && result.result.rateplans?.[0]) {
        setQuoteCache(prev => ({
          ...prev,
          [property.id]: {
            rateplan: result.result.rateplans[0],
            nights: result.result.nights,
          },
        }))
      } else {
        setQuoteCache(prev => ({ ...prev, [property.id]: 'failed' }))
      }
    } catch {
      setQuoteCache(prev => ({ ...prev, [property.id]: 'failed' }))
    }

    processingRef.current = false
    // Process next in queue
    processQueue()
  }, [feedId, state.visitorFilters])

  // Queue full price fetches when properties change
  useEffect(() => {
    if (!shouldFetchFullPrice || properties.length === 0) return

    // Reset cache and queue for new filter results
    setQuoteCache({})
    quoteQueueRef.current = [...properties]
    processingRef.current = false
    processQueue()
  }, [shouldFetchFullPrice, properties.length, processQueue])

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  )

  useEffect(() => {
    if (!options.infiniteScroll) return

    observerRef.current = new IntersectionObserver(handleObserver, {
      rootMargin: '200px',
    })

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current)
    }

    return () => {
      observerRef.current?.disconnect()
    }
  }, [handleObserver, options.infiniteScroll])

  if (isLoading) {
    return (
      <div className="hfse-properties-container">
        <div className="hfse-properties" style={gridVars}>
          {Array.from({ length: 12 }, (_, i) => (
            <PropertySkeleton key={i} index={i} />
          ))}
        </div>
      </div>
    )
  }

  // A data-fetch failure must NOT fall through to the empty state below —
  // that would show "No properties found." + recommendations as if the search
  // legitimately returned nothing (#740). Show the error in the results slot;
  // the FilterBar (a sibling in ExplorerContent) stays usable.
  if (isError) {
    return (
      <div className="hfse-properties-container">
        <WidgetDataError error={error} />
      </div>
    )
  }

  if (properties.length === 0) {
    return (
      <div className="hfse-properties-container">
        <div className="hfse-properties" style={gridVars}>
          {/* Zero-results rescue (#1265): an exact dated search with the
              flexible-dates module on gets verified alternative windows in
              place of the generic message; every other empty search keeps it. */}
          <ZeroResultsRescue
            filters={filtersWithBounds}
            fallback={(
              <div className="hfse-no-items">
                {options.noItemsText || _t('grid_no_results', 'No properties found.')}
              </div>
            )}
          />
        </div>
        <Recommendations />
      </div>
    )
  }

  return (
    <div className="hfse-properties-container">
      {/* Above a grid WITH results only — on an empty grid the rescue box owns the messaging (#1265). */}
      <AdjustedDatesNotice filters={filtersWithBounds} />
      <div className="hfse-properties" style={gridVars} ref={gridRef}>
        {properties.map((property, index) => {
          const cached = quoteCache[property.id]
          // A promo card follows the N-th, 2N-th, … REAL listing (1-based count).
          const promo = promoByPos.get(index + 1)
          return (
            <Fragment key={property.id}>
              <PropertyCard
                property={property}
                options={options}
                visitorFilters={state.visitorFilters}
                showFavoriteButton={showFavoriteButton}
                mapActions={state.mapVisible ? mapActions : null}
                quoteData={shouldFetchFullPrice && cached && cached !== 'loading' && cached !== 'failed' ? cached : undefined}
                quoteLoading={shouldFetchFullPrice && cached === 'loading'}
                quoteFailed={shouldFetchFullPrice && cached === 'failed'}
                compare={compareFor(property, shouldFetchFullPrice && cached && cached !== 'loading' && cached !== 'failed' ? cached : undefined)}
              />
              {promo && (
                <MarketingCard
                  card={promoCards[promo.cardIndex]}
                  cardId={promoCards[promo.cardIndex].id}
                  ordinal={promo.ordinal}
                  globalStyle={mc.globalStyle}
                  onCtaClick={onPromoCtaClick}
                />
              )}
            </Fragment>
          )
        })}

        {isFetchingNextPage &&
          Array.from({ length: 3 }, (_, i) => (
            <PropertySkeleton key={`loading-${i}`} index={properties.length + i} />
          ))
        }
      </div>

      {options.infiniteScroll && hasNextPage && (
        <div ref={loadMoreRef} style={{ height: 1 }} />
      )}

      {!options.infiniteScroll && options.showLoadMore !== false && hasNextPage && (
        // Alignment lives in CSS only (.hfse-load-more-container centers it):
        // an inline text-align would beat card.loadMoreAlign's stylesheet rule.
        <div className="hfse-load-more-container">
          <button
            className="hfse-load-more-btn"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? _t('grid_loading', 'Loading...') : _t('grid_load_more', 'Load More')}
          </button>
        </div>
      )}
    </div>
  )
}
